// src/shared/screens/auth/LoginView.jsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useI18n } from '../../../i18n/I18nProvider';
import MobileInputModal from '../../components/MobileInputModal';
import PwaInstallControl from '../../components/PwaInstallControl';
import { resolveAuthErrorMessage, logAuthFailure } from '../../utils/systemMessage';

/**
 * LoginView (Base: 375x812)
 * - public/assets:
 *   - /assets/login_bg.png
 *   - /assets/logo.svg
 * - Design updates:
 *   1) 모든 텍스트 사이즈 절반(기본 16 -> 8)
 *   2) 입력/버튼 라운드 3px
 *   3) 버튼 hover/active 네온 효과(테두리색 기준)
 *
 * ✅ Fix:
 * - onSubmit만 호출하고 끝나서 "로그인 후 로비 이동"이 안 되는 문제 해결
 * - onSubmit 이후 localStorage 토큰 존재 여부로 성공 판단 후 /lobby 이동
 * - 서버가 id를 기대하는 케이스 대비해서 { id: username }도 함께 전달
 */
export default function LoginView({
  logoUrl,
  onSubmit,
  onAccountRecovery,
  onContactAgent,
  onCreateAccount,
  loading = false,
  useMobileInputModal = false,
  mobileFontBoost = 0,

  // ✅ 추가 옵션 (필요하면 부모에서 덮어쓰기)
  lobbyPath = '/lobby',
  autoNavigateOnSuccess = true,

  // ✅ 토큰 키 후보(프로젝트별로 다를 수 있어서 후보로 검사)
  tokenKeys = ['token', 'accessToken', 'access_token', 'jwt', 'authToken'],
  renderVideoBackground = true,
  // Guest lobby login overlay only: 'desktop' | 'mobile'.
  // Keeps the standalone /login screen unchanged.
  guestPopupVariant = null,
}) {
  // [PATCH][ANCHOR:LOGINVIEW:I18N_HOOK_WITH_SETLANG]
  const { t, lang, setLang } =
    useI18n?.() || { t: (k) => k, lang: 'en', setLang: () => {} };

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [autoLogin, setAutoLogin] = useState(false);

  const [localBusy, setLocalBusy] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const logo = useMemo(() => logoUrl || '/assets/logo.svg', [logoUrl]);

  // ✅ 텍스트 사이즈 절반: 기본 16px -> 8px
  const baseFontSize = 8 + Number(mobileFontBoost || 0);

  const disabledCommonStyle = (loading || localBusy)
    ? { opacity: 0.6, cursor: 'not-allowed' }
    : undefined;

  const hasAnyToken = () => {
    try {
      for (const k of tokenKeys) {
        const v = localStorage.getItem(k);
        if (v && String(v).trim().length > 0) return true;
      }
    } catch (_) {}
    return false;
  };

  const goLobby = () => {
    try {
      // SPA navigate가 아니라도 확실히 보내기 위해 replace 사용
      window.location.replace(lobbyPath);
    } catch (_) {
      window.location.href = lobbyPath;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading || localBusy) return;

    setErrorMsg('');

    if (!username.trim()) {
      setErrorMsg(t('auth.login.error.idRequired', null, 'ID를 입력해주세요.'));
      return;
    }
    if (!password) {
      setErrorMsg(t('auth.login.error.passwordRequired', null, '비밀번호를 입력해주세요.'));
      return;
    }

    setLocalBusy(true);

    try {
      // ✅ 서버가 id를 기대하는 케이스 대비해서 둘 다 전달
      const payload = {
        username,
        id: username,
        password,
        autoLogin,
      };

      // onSubmit이 Promise를 반환해도/안 해도 대응
      const ret = onSubmit?.(payload);
      if (ret && typeof ret.then === 'function') {
        await ret;
      }

      // ✅ 성공 판단:
      // 1) onSubmit 내부에서 토큰 저장하는 구조라면 localStorage에 토큰이 생김
      // 2) 토큰 존재 시 로비로 이동
      if (autoNavigateOnSuccess && hasAnyToken()) {
        goLobby();
        return;
      }

      // 토큰이 없으면 실패 가능성이 높으니 사용자에게 힌트 제공
      // (부모에서 에러처리를 안 해도 여기서 최소한 표시)
      if (autoNavigateOnSuccess) {
        setErrorMsg(t('auth.login.error.tokenNotStored', null, '로그인에 실패했거나 토큰 저장이 되지 않았습니다.'));
      }
    } catch (err) {
      const msg = resolveAuthErrorMessage(
        t,
        err,
        t('auth.login.error.failed', null, 'Login failed.')
      );
      setErrorMsg(String(msg));
      // eslint-disable-next-line no-console
      logAuthFailure('login', err);
    } finally {
      setLocalBusy(false);
    }
  };

  if (guestPopupVariant === 'desktop' || guestPopupVariant === 'mobile') {
    const isGuestMobile = guestPopupVariant === 'mobile';

    // Guest-login composition is independent from the old /login page.
    // Desktop uses a larger 510x854 canvas (about 1.5x the prior popup, with extra width).
    // Mobile keeps the existing proportions; only the logo is capped so tablet widths cannot inflate it.
    const fieldWidth = isGuestMobile ? 'max(52.6%, 190px)' : 318;
    const compactFontSize = isGuestMobile ? 9 : 12;
    const compactButtonFontSize = isGuestMobile ? 9 : 12;
    const inputHeight = isGuestMobile ? 'clamp(28px, 3.2dvh, 41px)' : 36;
    const signInHeight = isGuestMobile ? 'clamp(30px, 3.6dvh, 46px)' : 41;
    const secondaryHeight = isGuestMobile ? 'clamp(28px, 3.125dvh, 40px)' : 36;
    const autoHeight = isGuestMobile ? 'clamp(23px, 2.75dvh, 35px)' : 30;
    const logoWidth = isGuestMobile ? '27.5%' : 112;
    const logoHeight = isGuestMobile ? 'auto' : 60;

    return (
      <div
        data-npc-guest-login-view={guestPopupVariant}
        style={{
          width: '100%',
          height: '100%',
          position: 'relative',
          overflow: 'hidden',
          background: '#000',
          color: '#fff',
          fontSize: compactFontSize,
          lineHeight: 1.2,
          userSelect: 'none',
          WebkitUserSelect: 'none',
          WebkitTouchCallout: 'none',
        }}
      >
        <style>{`
          [data-npc-guest-login-view] * { box-sizing: border-box; }
          .npc-guest-login-btn {
            border-radius: 2px;
            transition: box-shadow .18s ease, border-color .18s ease, transform .12s ease;
            outline: none;
            -webkit-tap-highlight-color: transparent;
          }
          .npc-guest-login-btn:active { transform: scale(.985); }
          .npc-guest-login-btn:disabled { opacity:.6; cursor:not-allowed !important; box-shadow:none !important; transform:none; }
          .npc-guest-login-btn.npc-guest-orange:hover {
            border-color:#ffd27d !important;
            box-shadow:0 0 8px rgba(255,167,45,.38);
          }
          .npc-guest-login-btn.npc-guest-green:hover {
            border-color:#7cffc0 !important;
            box-shadow:0 0 8px rgba(45,255,125,.28);
          }
          .npc-guest-login-input {
            user-select:text !important;
            -webkit-user-select:text !important;
            -webkit-touch-callout:default;
          }
          .npc-guest-login-input::placeholder { color:rgba(255,255,255,.30); }
          .npc-guest-login-video {
            position:absolute;
            inset:0;
            width:100%;
            height:100%;
            object-fit:cover;
            object-position:center center;
            background:#000;
            user-select:none;
            -webkit-user-select:none;
            pointer-events:none;
          }
          .npc-guest-login-mesh {
            position:absolute;
            inset:0;
            pointer-events:none;
            opacity:.42;
            mix-blend-mode:multiply;
            background-image:
              linear-gradient(rgba(0,0,0,.17) 1px,transparent 1px),
              linear-gradient(90deg,rgba(0,0,0,.17) 1px,transparent 1px);
            background-size:3px 3px;
          }
        `}</style>

        {/* Top 40%: video is the popup itself, not a floating child card. */}
        <div
          aria-hidden="true"
          style={{
            position: 'absolute',
            left: 0,
            top: 0,
            right: 0,
            height: '40%',
            overflow: 'hidden',
            background: '#000',
          }}
        >
          <video
            autoPlay
            loop
            muted
            playsInline
            preload="metadata"
            draggable={false}
            className="npc-guest-login-video"
          >
            <source src="/assets/test.mp4" type="video/mp4" />
          </video>

          {/* Vignette + the soft black transition shown in the final references. */}
          <div
            style={{
              position: 'absolute',
              inset: 0,
              pointerEvents: 'none',
              background: isGuestMobile
                ? 'radial-gradient(circle at 52% 42%, rgba(255,255,255,.08), transparent 26%), linear-gradient(90deg, rgba(0,0,0,.30) 0%, rgba(0,0,0,.02) 48%, rgba(0,0,0,.30) 100%), linear-gradient(180deg, rgba(0,0,0,.04) 0%, rgba(0,0,0,.08) 67%, rgba(0,0,0,.52) 88%, #000 100%)'
                : 'radial-gradient(circle at 52% 42%, rgba(255,255,255,.09), transparent 25%), linear-gradient(90deg, rgba(0,0,0,.34) 0%, rgba(0,0,0,.02) 48%, rgba(0,0,0,.34) 100%), linear-gradient(180deg, rgba(0,0,0,.04) 0%, rgba(0,0,0,.08) 67%, rgba(0,0,0,.58) 88%, #000 100%)',
            }}
          />
          <div className="npc-guest-login-mesh" />
        </div>

        {/* Bottom 60%: black login panel, slightly overlapping the video for a soft gradient seam. */}
        <div
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            top: '37.5%',
            bottom: 0,
            display: isGuestMobile ? 'block' : 'flex',
            flexDirection: isGuestMobile ? undefined : 'column',
            alignItems: isGuestMobile ? undefined : 'center',
            justifyContent: isGuestMobile ? undefined : 'flex-start',
            paddingTop: isGuestMobile ? 0 : 34,
            background: isGuestMobile
              ? 'linear-gradient(180deg, rgba(0,0,0,0) 0px, rgba(0,0,0,.74) 15px, #000 29px, #000 100%)'
              : 'linear-gradient(180deg, rgba(0,0,0,0) 0px, rgba(0,0,0,.76) 13px, #000 27px, #000 100%)',
            zIndex: 5,
          }}
        >
          <img
            src={logo}
            alt={t('common.logoAlt', null, 'logo')}
            draggable={false}
            style={{
              position: isGuestMobile ? 'absolute' : 'static',
              top: isGuestMobile ? '4.5%' : undefined,
              left: isGuestMobile ? '50%' : undefined,
              transform: isGuestMobile ? 'translateX(-50%)' : undefined,
              width: logoWidth,
              height: logoHeight,
              objectFit: 'contain',
              display: 'block',
              marginBottom: 0,
              maxWidth: isGuestMobile ? 128 : undefined,
              minWidth: isGuestMobile ? 92 : undefined,
              userSelect: 'none',
              WebkitUserSelect: 'none',
              pointerEvents: 'none',
            }}
          />

          <form
            onSubmit={handleSubmit}
            style={{
              position: isGuestMobile ? 'absolute' : 'static',
              top: isGuestMobile ? '23.5%' : undefined,
              left: isGuestMobile ? '50%' : undefined,
              transform: isGuestMobile ? 'translateX(-50%)' : undefined,
              width: fieldWidth,
              maxWidth: isGuestMobile ? 'calc(100% - 48px)' : undefined,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'stretch',
            }}
          >
            <label
              style={{
                textAlign: 'center',
                fontSize: compactFontSize,
                lineHeight: isGuestMobile ? '11px' : '15px',
                marginBottom: isGuestMobile ? 'clamp(6px, .48dvh, 7px)' : 11,
              }}
            >
              {t('auth.login.username.label')}
            </label>
            <CompactGuestInput
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder={t('auth.login.username.placeholder')}
              type="text"
              autoComplete="username"
              disabled={loading || localBusy}
              fontSize={compactFontSize}
              height={inputHeight}
              useMobileInputModal={useMobileInputModal}
              modalTitle={t('auth.login.username.inputModalTitle', null, '아이디 입력')}
              modalDescription={t('auth.login.username.inputModalGuide', null, '로그인할 아이디를 입력해주세요.')}
            />

            <div style={{ height: isGuestMobile ? 'clamp(14px, 2.1dvh, 27px)' : 11, flex: '0 0 auto' }} />
            <label
              style={{
                textAlign: 'center',
                fontSize: compactFontSize,
                lineHeight: isGuestMobile ? '11px' : '15px',
                marginBottom: isGuestMobile ? 'clamp(8px, .95dvh, 12px)' : 5,
              }}
            >
              {t('auth.login.password.label', null, 'PASSWORD')}
            </label>
            <CompactGuestInput
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={t('auth.login.password.placeholder')}
              type="password"
              autoComplete="current-password"
              disabled={loading || localBusy}
              fontSize={compactFontSize}
              height={inputHeight}
              useMobileInputModal={useMobileInputModal}
              modalTitle={t('auth.login.password.inputModalTitle', null, '비밀번호 입력')}
              modalDescription={t('auth.login.password.inputModalGuide', null, '로그인 비밀번호를 입력해주세요.')}
            />

            {/* Keep the password group close to Sign in. With no error, this gap is
                exactly the same as Sign in -> secondary actions. */}
            {errorMsg ? (
              <div
                style={{
                  minHeight: isGuestMobile ? 18 : 22,
                  marginTop: isGuestMobile ? 6 : 7,
                  marginBottom: isGuestMobile ? 6 : 7,
                  flex: '0 0 auto',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'hidden',
                  color: '#ff7777',
                  textAlign: 'center',
                  fontSize: isGuestMobile ? 8 : 10,
                  lineHeight: 1.1,
                }}
              >
                {errorMsg}
              </div>
            ) : (
              <div style={{ height: isGuestMobile ? 'clamp(8px, 1.1dvh, 14px)' : 12, flex: '0 0 auto' }} />
            )}

            <button
              type="submit"
              disabled={loading || localBusy}
              className="npc-guest-login-btn npc-guest-orange"
              style={{
                width: '100%',
                height: signInHeight,
                flex: '0 0 auto',
                background: 'rgba(0,0,0,.78)',
                border: '1px solid #FFA72D',
                color: '#fff',
                fontSize: compactButtonFontSize,
                cursor: (loading || localBusy) ? 'not-allowed' : 'pointer',
              }}
            >
              {loading || localBusy ? t('auth.login.signingIn', null, 'Signing in…') : t('auth.login.submit', null, 'Sign in')}
            </button>

            <div style={{ height: isGuestMobile ? 'clamp(8px, 1.1dvh, 14px)' : 12, flex: '0 0 auto' }} />
            <div style={{ display: 'flex', gap: isGuestMobile ? 12 : 11, height: secondaryHeight, flex: '0 0 auto' }}>
              <button
                type="button"
                disabled={loading || localBusy}
                className="npc-guest-login-btn npc-guest-green"
                onClick={() => {
                  if (loading || localBusy) return;
                  const handler = onAccountRecovery || onContactAgent;
                  handler?.({ username, id: username, autoLogin });
                }}
                style={{
                  flex: 1,
                  minWidth: 0,
                  height: '100%',
                  background: 'rgba(0,0,0,.78)',
                  border: '1px solid #00ff66',
                  color: '#fff',
                  fontSize: compactButtonFontSize,
                  cursor: (loading || localBusy) ? 'not-allowed' : 'pointer',
                }}
              >
                {t('auth.login.accountRecovery', null, '계정 찾기')}
              </button>
              <button
                type="button"
                disabled={loading || localBusy}
                className="npc-guest-login-btn npc-guest-green"
                onClick={() => { if (!(loading || localBusy)) onCreateAccount?.({ username, id: username }); }}
                style={{
                  flex: 1,
                  minWidth: 0,
                  height: '100%',
                  background: 'rgba(0,0,0,.78)',
                  border: '1px solid #00ff66',
                  color: '#fff',
                  fontSize: compactButtonFontSize,
                  cursor: (loading || localBusy) ? 'not-allowed' : 'pointer',
                }}
              >
                {t('auth.login.createAccount')}
              </button>
            </div>

            <div style={{ height: isGuestMobile ? 'clamp(7px, .95dvh, 12px)' : 11, flex: '0 0 auto' }} />
            <label
              style={{
                width: '100%',
                height: autoHeight,
                flex: '0 0 auto',
                background: 'rgba(0,0,0,.84)',
                border: '1px solid #3c3c3c',
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                padding: isGuestMobile ? '0 10px' : '0 12px',
                gap: isGuestMobile ? 10 : 11,
                color: '#fff',
                fontSize: compactFontSize,
                cursor: (loading || localBusy) ? 'not-allowed' : 'pointer',
              }}
            >
              <input
                type="checkbox"
                checked={autoLogin}
                disabled={loading || localBusy}
                onChange={(e) => setAutoLogin(e.target.checked)}
                style={{
                  width: isGuestMobile ? 12 : 15,
                  height: isGuestMobile ? 12 : 15,
                  margin: 0,
                  flex: '0 0 auto',
                }}
              />
              <span>{t('auth.login.autoSignIn')}</span>
            </label>

            <div
              className="npc-guest-pwa"
              style={{
                width: '100%',
                marginTop: isGuestMobile ? 'clamp(7px, .95dvh, 12px)' : 11,
                flex: '0 0 auto',
              }}
            >
              <style>{`
                [data-npc-guest-login-view] .npc-guest-pwa .npc-pwa-login-download-wrap {
                  width: 100%;
                  margin-top: 0;
                }
                [data-npc-guest-login-view] .npc-guest-pwa .npc-pwa-login-download {
                  width: 100%;
                  height: ${isGuestMobile ? 'clamp(28px, 3.125dvh, 40px)' : '36px'};
                  font-size: ${compactButtonFontSize}px;
                }
              `}</style>
              <PwaInstallControl
                baseFontSize={compactButtonFontSize}
                disabled={loading || localBusy}
              />
            </div>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        width: 375,
        height: 812,
        position: 'relative',
        background: renderVideoBackground ? '#000000' : 'transparent',
        overflow: 'hidden',
        fontSize: baseFontSize,
        lineHeight: 1.2,
      }}
    >
      {/* 버튼 네온 CSS (hover/active는 inline로 못하므로 style tag로) */}
      <style>{`
        .npc-btn {
          border-radius: 3px;
          transition: box-shadow .18s ease, border-color .18s ease, transform .12s ease;
          outline: none;
        }
        .npc-btn:disabled {
          box-shadow: none !important;
          transform: none !important;
        }

        /* ORANGE (Sign in) */
        .npc-orange:hover {
          border-color: #FFD27D !important;
          box-shadow:
            0 0 3px rgba(255,167,45,.70),
            0 0 8px rgba(255,167,45,.45),
            0 0 14px rgba(255,167,45,.25);
        }
        .npc-orange:active {
          transform: scale(.97);
          box-shadow:
            0 0 2px rgba(255,167,45,.90),
            0 0 6px rgba(255,167,45,.60);
        }

        /* GREEN (Agent/New) */
        .npc-green:hover {
          border-color: #7CFFC0 !important;
          box-shadow:
            0 0 3px rgba(45,255,125,.65),
            0 0 8px rgba(45,255,125,.40),
            0 0 14px rgba(45,255,125,.22);
        }
        .npc-green:active {
          transform: scale(.97);
          box-shadow:
            0 0 2px rgba(45,255,125,.85),
            0 0 6px rgba(45,255,125,.55);
        }

        /* Placeholder color */
        .npc-input::placeholder {
          color: rgba(255,255,255,0.45);
        }

        /* Login video background: same video + mesh feel as site.zip */
        .npc-login-video {
          position: absolute;
          inset: 0;
          z-index: 0;
          width: 100%;
          height: 100%;
          object-fit: cover;
          background: #000;
        }
        .npc-login-video-overlay {
          position: absolute;
          inset: 0;
          z-index: 1;
          pointer-events: none;
          background:
            radial-gradient(circle at 52% 42%, rgba(255, 255, 255, 0.16), transparent 22%),
            linear-gradient(180deg, rgba(0, 0, 0, 0.16) 0%, rgba(0, 0, 0, 0.32) 52%, rgba(0, 0, 0, 0.72) 100%),
            linear-gradient(90deg, rgba(0, 0, 0, 0.54) 0%, rgba(0, 0, 0, 0.06) 45%, rgba(0, 0, 0, 0.58) 100%),
            rgba(0, 0, 0, 0.14);
        }
        .npc-login-video-mesh {
          position: absolute;
          inset: 0;
          z-index: 2;
          pointer-events: none;
          background-image:
            linear-gradient(rgba(0, 0, 0, 0.16) 1px, transparent 1px),
            linear-gradient(90deg, rgba(0, 0, 0, 0.16) 1px, transparent 1px);
          background-size: 3px 3px;
          opacity: 0.45;
          mix-blend-mode: multiply;
        }
      `}</style>

      {renderVideoBackground ? (
        <>
          <video
            aria-hidden="true"
            autoPlay
            className="npc-login-video"
            loop
            muted
            playsInline
            preload="metadata"
          >
            <source src="/assets/test.mp4" type="video/mp4" />
          </video>
          <div className="npc-login-video-overlay" aria-hidden="true" />
          <div className="npc-login-video-mesh" aria-hidden="true" />
        </>
      ) : null}

      <style>{`
          {/* [ADD][ANCHOR:LOGINVIEW:LANG_DROPDOWN_CSS] */}
        .npc-lang-wrap{
          position:absolute;
          right:10px;
          top:10px;
          z-index:50;
        }

        .npc-lang-btn{
          height:20px;
          min-width:120px;
          width:100%;                 /* ✅ wrap 폭에 맞춤 */
          padding:0 8px;
          display:flex;
          align-items:center;
          justify-content:space-between;
          gap:8px;
          background:rgba(0,0,0,0.35);
          border:1px solid rgba(255,255,255,0.35);
          border-radius:3px;
          color:#fff;
          cursor:pointer;
          box-sizing:border-box;
        }
        .npc-lang-btn:hover{ border-color:rgba(255,255,255,0.60); }
        .npc-lang-btn:disabled{ opacity:0.55; cursor:not-allowed; }

        .npc-lang-menu{
          margin-top:6px;
          width:100%;                 /* ✅ 버튼 폭과 동일 */
          max-height:330px;
          overflow:auto;
          background:rgba(0,0,0,0.75);
          border:1px solid rgba(255,255,255,0.25);
          border-radius:3px;
          box-shadow:0 10px 25px rgba(0,0,0,0.35);
        }

        /* ✅ 스크롤바: track 투명 / thumb #000 / radius 5 / width 3 */
        .npc-lang-menu{ scrollbar-color:#000 transparent; scrollbar-width:thin; } /* firefox */
        .npc-lang-menu::-webkit-scrollbar{ width:3px; }
        .npc-lang-menu::-webkit-scrollbar-track{ background:transparent; }
        .npc-lang-menu::-webkit-scrollbar-thumb{
          background:#000;
          border-radius:5px;
        }

        .npc-lang-item{
          width:100%;
          padding:6px 8px;
          display:flex;
          align-items:center;
          gap:8px;
          background:transparent;
          border:0;
          color:#fff;
          cursor:pointer;
          text-align:left;
          box-sizing:border-box;
        }
        .npc-lang-item:hover{ background:rgba(255,255,255,0.08); }
      `}</style>


      {/* Layer 1 Frame: 361x796, left 7, top 8 */}
      <div
        style={{
          position: 'absolute',
          left: 7,
          top: 8,
          width: 361,
          height: 796,
          background: 'transparent',
          border: 'none',
          boxSizing: 'border-box',
          zIndex: 3,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        

       {/* [ADD][ANCHOR:LOGINVIEW:LANGUAGE_COMBOBOX_TOPRIGHT] */}
       <LanguageComboBox
          value={String(lang || 'en')}
          onChange={(next) => setLang?.(next)}
          disabled={loading || localBusy}
          baseFontSize={baseFontSize}
          ariaLabel={t('common.language', null, 'language')}
        />

        {/* 13 */}
        <div style={{ height: 13 }} />

        {/* Title */}
        <div style={{ color: '#fff', fontSize: baseFontSize }}>
          NIGHT CLUB Network
        </div>

        {/* 54 */}
        <div style={{ height: 54 }} />

        {/* Logo 240x160 */}
        <div
          style={{
            width: 240,
            height: 160,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            userSelect: 'none',
            pointerEvents: 'none',
          }}
        >
          <img
            src={logo}
            alt={t('common.logoAlt', null, 'logo')}
            style={{ width: 240, height: 160, objectFit: 'contain', display: 'block' }}
            draggable={false}
          />
        </div>

        {/* 54 */}
        <div style={{ height: 54 }} />

        <form
          onSubmit={handleSubmit}
          style={{
            width: 240,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            fontSize: baseFontSize,
          }}
        >
          {/* ID Label */}
          <div style={{ width: '100%', textAlign: 'center', color: '#fff', fontSize: baseFontSize }}>
            {t('auth.login.username.label')}
          </div>

          {/* 14 */}
          <div style={{ height: 14 }} />

          {/* ID Input 240x29 */}
          <InputBox
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            placeholder={t('auth.login.username.placeholder')}
            type="text"
            autoComplete="username"
            disabled={loading || localBusy}
            fontSize={baseFontSize}
            useMobileInputModal={useMobileInputModal}
            modalTitle={t('auth.login.username.inputModalTitle', null, '아이디 입력')}
            modalDescription={t('auth.login.username.inputModalGuide', null, '로그인할 아이디를 입력해주세요.')}
          />

          {/* 41 */}
          <div style={{ height: 41 }} />

          {/* PASSWORD Label */}
          <div style={{ width: '100%', textAlign: 'center', color: '#fff', fontSize: baseFontSize }}>
            {t('auth.login.password.label', null, 'PASSWORD')}
          </div>

          {/* 9 */}
          <div style={{ height: 9 }} />

          {/* PW Input 240x29 */}
          <InputBox
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={t('auth.login.password.placeholder')}
            type="password"
            autoComplete="current-password"
            disabled={loading || localBusy}
            fontSize={baseFontSize}
            useMobileInputModal={useMobileInputModal}
            modalTitle={t('auth.login.password.inputModalTitle', null, '비밀번호 입력')}
            modalDescription={t('auth.login.password.inputModalGuide', null, '로그인 비밀번호를 입력해주세요.')}
          />

          {/* 32 */}
          <div style={{ height: 32 }} />

          {/* Sign in 240x32 */}
          <button
            type="submit"
            disabled={loading || localBusy}
            className="npc-btn npc-orange"
            style={{
              width: 240,
              height: 32,
              background: 'rgba(0,0,0,0.5)',
              border: '1px solid #FFA72D',
              color: '#fff',
              boxSizing: 'border-box',
              fontSize: baseFontSize,
              borderRadius: 3,
              ...(disabledCommonStyle || { cursor: 'pointer' }),
            }}
          >
            {loading || localBusy ? t('auth.login.signingIn', null, 'Signing in…') : t('auth.login.submit', null, 'Sign in')}
          </button>

          {/* 에러 메시지 */}
          {errorMsg ? (
            <>
              <div style={{ height: 8 }} />
              <div
                style={{
                  width: 240,
                  textAlign: 'center',
                  color: '#ff5a5a',
                  fontSize: baseFontSize,
                  textShadow: '0 0 8px rgba(255,0,0,0.25)',
                  userSelect: 'none',
                }}
              >
                {errorMsg}
              </div>
            </>
          ) : null}

          {/* 10 */}
          <div style={{ height: 10 }} />

          {/* Agent/New row: 114 + 12 + 114 = 240 */}
          <div style={{ width: 240, display: 'flex', gap: 12 }}>
            <button
              type="button"
              disabled={loading || localBusy}
              className="npc-btn npc-green"
              style={{
                ...greenBtnStyle,
                fontSize: baseFontSize,
                borderRadius: 3,
                ...(disabledCommonStyle || { cursor: 'pointer' }),
              }}
              onClick={() => {
                if (loading || localBusy) return;
                const handler = onAccountRecovery || onContactAgent;
                handler?.({ username, id: username, autoLogin });
              }}
            >
              {t('auth.login.accountRecovery', null, '계정 찾기')}
            </button>

            <button
              type="button"
              disabled={loading || localBusy}
              className="npc-btn npc-green"
              style={{
                ...greenBtnStyle,
                fontSize: baseFontSize,
                borderRadius: 3,
                ...(disabledCommonStyle || { cursor: 'pointer' }),
              }}
              onClick={() => {
                if (loading || localBusy) return;
                onCreateAccount?.({ username, id: username });
              }}
            >
              {t('auth.login.createAccount')}
            </button>
          </div>

          {/* 12 */}
          <div style={{ height: 12 }} />

          {/* Auto log-in: checkbox + box (bg 50%, border #3c3c3c) */}
          <div
            style={{
              width: 240,
              height: 29,
              background: 'rgba(0,0,0,0.8)',
              border: '1px solid #3c3c3c',
              borderRadius: 3,
              boxSizing: 'border-box',
              display: 'flex',
              alignItems: 'center',
              padding: '0 10px',
              gap: 10,
              color: '#fff',
              fontSize: baseFontSize,
              ...(loading || localBusy ? { opacity: 0.6 } : undefined),
            }}
          >
            <input
              type="checkbox"
              checked={autoLogin}
              disabled={loading || localBusy}
              onChange={(e) => setAutoLogin(e.target.checked)}
              style={{
                width: 12,
                height: 12,
                margin: 0,
                cursor: (loading || localBusy) ? 'not-allowed' : 'pointer',
              }}
            />
            <span style={{ userSelect: 'none' }}>{t('auth.login.autoSignIn')}</span>
          </div>

          <PwaInstallControl
            baseFontSize={baseFontSize}
            disabled={loading || localBusy}
          />

          {/* Standalone /login entry only; never display this inside the guest login popup. */}
          <button
            type="button"
            className="npc-login-guest-lobby-link"
            disabled={loading || localBusy}
            onClick={() => {
              if (loading || localBusy) return;
              // A deliberate guest entry must not immediately reopen the login overlay.
              // This is the same per-tab dismissal preference used by GuestLoginOverlay.
              try { sessionStorage.setItem('npc:guest-login:dismissed', '1'); } catch (_) {}
              try { window.location.assign(lobbyPath); } catch (_) { window.location.href = lobbyPath; }
            }}
            style={{
              width: 240,
              minHeight: 24,
              marginTop: 12,
              padding: '4px 6px',
              background: 'transparent',
              border: 'none',
              color: 'rgba(245,245,250,.86)',
              textDecoration: 'underline',
              textUnderlineOffset: 3,
              textDecorationColor: 'rgba(245,245,250,.28)',
              fontSize: baseFontSize + 2,
              fontWeight: 500,
              cursor: loading || localBusy ? 'not-allowed' : 'pointer',
              opacity: loading || localBusy ? 0.55 : 1,
            }}
          >
            {t('auth.login.enterLobbyWithoutLogin', null, 'Enter the lobby without signing in')}
          </button>
        </form>

        {/* 222 */}
        <div style={{ height: 222 }} />
      </div>
    </div>
  );
}

// [ADD][ANCHOR:LOGINVIEW:LANGUAGE_COMBOBOX_COMPONENT]
const NPC_LANG_ITEMS = [
  { flag: 'Bangladesh',       label: 'Bangladesh',       lang: 'bn' },
  { flag: 'Brazil',           label: 'Brazil',           lang: 'pt-br' },
  { flag: 'China',            label: 'China',            lang: 'zh-cn' },
  { flag: 'France',           label: 'France',           lang: 'fr' },
  { flag: 'Greece',           label: 'Greece',           lang: 'el' },
  { flag: 'India',            label: 'India',            lang: 'hi' },
  { flag: 'Indonesia',        label: 'Indonesia',        lang: 'id' },
  { flag: 'Italy',            label: 'Italy',            lang: 'it' },
  { flag: 'Japan',            label: 'Japan',            lang: 'ja' },
  { flag: 'Korea',            label: 'Korea',            lang: 'ko' },      // ✅ 현재 한글 JSON 즉시 적용
  { flag: 'Malaysia',         label: 'Malaysia',         lang: 'ms' },
  { flag: 'Mongolia',         label: 'Mongolia',         lang: 'mn' },
  { flag: 'Mexico',           label: 'Mexico',           lang: 'es-mx' },
  { flag: 'Norway',           label: 'Norway',           lang: 'nb-no' },
  { flag: 'Pakistan',         label: 'Pakistan',         lang: 'ur' },
  { flag: 'Philippines',      label: 'Philippines',      lang: 'fil' },
  { flag: 'Portugal',         label: 'Portugal',         lang: 'pt' },
  { flag: 'Russia',           label: 'Russia',           lang: 'ru' },
  { flag: 'Spain',            label: 'Spain',            lang: 'es' },
  { flag: 'Thailand',         label: 'Thailand',         lang: 'th' },
  { flag: 'Turkey',           label: 'Turkey',           lang: 'tr' },
  { flag: 'United_Kingdom',   label: 'United Kingdom',   lang: 'en-gb' },
  { flag: 'Vietnam',          label: 'Vietnam',          lang: 'vi' },
];

function LanguageComboBox({ value, onChange, disabled = false, baseFontSize = 8, ariaLabel = 'language' }) {
  const wrapRef = useRef(null);
  const [open, setOpen] = useState(false);

  const current = useMemo(() => {
    const v = String(value || '').toLowerCase();
    return (
      NPC_LANG_ITEMS.find((x) => String(x.lang).toLowerCase() === v) ||
      NPC_LANG_ITEMS.find((x) => String(x.lang).toLowerCase() === 'en-gb') ||
      NPC_LANG_ITEMS[0]
    );
  }, [value]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      const el = wrapRef.current;
      if (!el) return;
      if (!el.contains(e.target)) setOpen(false);
    };
    window.addEventListener('mousedown', onDown);
    return () => window.removeEventListener('mousedown', onDown);
  }, [open]);

  return (
    <div className="npc-lang-wrap" ref={wrapRef}>
      <button
        type="button"
        className="npc-lang-btn"
        disabled={disabled}
        onClick={() => {
          if (disabled) return;
          setOpen((v) => !v);
        }}
        style={{ fontSize: baseFontSize }}
        aria-label={ariaLabel}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <img
            src={`/assets/flags/${current.flag}.svg`}
            alt=""
            style={{ width: 18, height: 12, display: 'block' }}
            draggable={false}
          />
          <span style={{ whiteSpace: 'nowrap', opacity: 0.95 }}>{current.label}</span>
        </span>
        <span style={{ opacity: 0.8 }}>▾</span>
      </button>

      {open ? (
        <div className="npc-lang-menu">
          {NPC_LANG_ITEMS.map((it) => {
            const selected = String(it.lang).toLowerCase() === String(value || '').toLowerCase();
            return (
              <button
                key={it.flag}
                type="button"
                className="npc-lang-item"
                onClick={() => {
                  onChange?.(it.lang);
                  setOpen(false);
                }}
                style={{ fontSize: baseFontSize }}
              >
                <img
                  src={`/assets/flags/${it.flag}.svg`}
                  alt=""
                  style={{ width: 18, height: 12, display: 'block' }}
                  draggable={false}
                />
                <span style={{ flex: 1 }}>{it.label}</span>
                {selected ? <span style={{ opacity: 0.9 }}>✓</span> : null}
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}


function CompactGuestInput({
  value,
  onChange,
  placeholder,
  type = 'text',
  autoComplete,
  disabled = false,
  fontSize = 8,
  height = 28,
  useMobileInputModal = false,
  modalTitle,
  modalDescription,
}) {
  const commonStyle = {
    width: '100%',
    height: '100%',
    border: 'none',
    outline: 'none',
    background: 'transparent',
    color: '#fff',
    padding: '0 10px',
    boxSizing: 'border-box',
    fontSize,
    lineHeight: 1.2,
    cursor: disabled ? 'not-allowed' : 'text',
  };

  return (
    <div
      style={{
        width: '100%',
        height,
        background: 'rgba(0,0,0,.72)',
        border: '1px solid #3c3c3c',
        borderRadius: 2,
        boxSizing: 'border-box',
        opacity: disabled ? 0.7 : 1,
        overflow: 'hidden',
      }}
    >
      {useMobileInputModal ? (
        <MobileInputModal
          className="npc-guest-login-input"
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          type={type}
          modalInputType={type === 'password' ? 'password' : 'text'}
          autoComplete={autoComplete}
          disabled={disabled}
          modalTitle={modalTitle}
          modalDescription={modalDescription}
          triggerAsButton
          style={commonStyle}
        />
      ) : (
        <input
          className="npc-guest-login-input"
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          type={type}
          autoComplete={autoComplete}
          disabled={disabled}
          style={commonStyle}
        />
      )}
    </div>
  );
}

function InputBox({
  value,
  onChange,
  placeholder,
  type = 'text',
  autoComplete,
  disabled = false,
  fontSize = 8,
  useMobileInputModal = false,
  modalTitle,
  modalDescription,
}) {
  return (
    <div
      style={{
        width: 240,
        height: 29,
        background: 'rgba(0,0,0,0.5)',
        border: '1px solid #3c3c3c',
        borderRadius: 3,
        boxSizing: 'border-box',
        display: 'flex',
        alignItems: 'center',
        padding: '0 10px',
        opacity: disabled ? 0.7 : 1,
      }}
    >
      {useMobileInputModal ? (
        <MobileInputModal
          className="npc-input"
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          type={type}
          modalInputType={type === 'password' ? 'password' : 'text'}
          autoComplete={autoComplete}
          disabled={disabled}
          modalTitle={modalTitle}
          modalDescription={modalDescription}
          triggerAsButton
          style={{
            width: '100%',
            height: '100%',
            border: 'none',
            outline: 'none',
            background: 'transparent',
            color: '#fff',
            padding: 0,
            fontSize,
            lineHeight: 1.2,
            cursor: disabled ? 'not-allowed' : 'text',
          }}
        />
      ) : (
        <input
          className="npc-input"
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          type={type}
          autoComplete={autoComplete}
          disabled={disabled}
          style={{
            width: '100%',
            height: '100%',
            border: 'none',
            outline: 'none',
            background: 'transparent',
            color: '#fff',
            padding: 0,
            fontSize,
            lineHeight: 1.2,
            cursor: disabled ? 'not-allowed' : 'text',
          }}
        />
      )}
    </div>
  );
}

const greenBtnStyle = {
  width: 114,
  height: 32,
  background: 'rgba(0,0,0,0.5)',
  border: '1px solid #2DFF7D',
  color: '#fff',
  boxSizing: 'border-box',
};
